import React, { useContext, useState, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { withRouter, useHistory } from 'react-router-dom'
import { App, Tabs, TabsProps, Tooltip } from 'antd'
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getLocationByIdLeaveTypesAllLeavePolicies } from '../../../../graphql/custom-queries'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { useEffectivePlan } from '../../../../store/use-effective-plan'
import * as logger from '../../../../services/logger'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { redirectIfEntityNotFound } from '../../../../util/entity-not-found-redirect'
import CircularProgress from '../../../../components/circular-progress'
import LocationGeneral from './tabs/LocationGeneral'
import { useLocationFieldSave } from './tabs/LocationGeneral/use-location-field-save'
import { LocationUsers } from './tabs/LocationUsers'
import { InlineEditGroupContext, useInlineEditGroupState } from '../../../../components/inline-edit/context'
import { useUnsavedChangesGuard } from '../../../../hooks/use-unsaved-changes-guard'
import { useLocationAdmin } from '../../../../hooks/use-location-admin'
import { Banner } from '../../../../design-system/Banner'
import { Button } from '../../../../design-system/Button'
import { ConfirmModal } from '../../../../design-system/ConfirmModal'
import { PageHeader } from '../../../../design-system/PageHeader'
import { StatusBadge } from '../../../../design-system/StatusBadge'
import { RecordCardMissing } from '../../../../design-system/RecordCard'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import LocationHolidays from '../../../../components/location-holidays'
import type { PostedEvent } from '../../../../components/location-holidays'
import LocationLeavePolicies from '../../../../components/location-leave-policies'
import { settledOutcome, watchEvent } from '../../../../util/notifications'
import type { EventOutcome } from '../../../../util/notifications'

import {
  IGetLocationLeaveTypesAllLeavePoliciesData,
  IGetLocationList,
  IGetLeaveTypeList,
  IGetLocationGeneral,
  ILocationGeneralLeavePolicies,
  IHolidaysByYear,
} from '../../../../types/locations'
import type { HolidayRow, HolidayRowsByYear } from '../../../../components/location-holidays/logic'
import { PENDING_EDIT_TTL_MS } from '../../../../components/location-holidays/pending-edits'
import { ILocationHolidayDeletedEvent, ILocationHolidaysDeletedEvent } from '@vacationtracker/shared/types/holidays'
import { autodetectedTeams, countHolidaysThisYear, isAutodetectedName } from '../../../../util/location-facts'
import { getTimeZoneOrDefaultToUtc } from '@vacationtracker/shared/functions/timezone'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { selectLocaleSlice } from '../../../../store/locale-slice'
import { decorateLeavePolicyWithQuotaUnits } from '../../../../types/leave-types'
import { isWorkDayFeatureAllowed } from '@vacationtracker/shared/functions/work-week'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'

const LOCATION_TABS = ['general', 'leave-policies', 'holidays', 'users'] as const
type LocationTab = (typeof LOCATION_TABS)[number]

const isLocationTab = (value?: string): value is LocationTab => LOCATION_TABS.includes(value as LocationTab)

const EMPTY_HOLIDAYS: HolidayRowsByYear[] = []

/** How long a holiday event may go unreported before the tab stops waiting on it. */
const EVENT_SETTLE_TIMEOUT_MS = 15_000

// Mutates byYear in place and returns it.
const groupMultiDayHolidays = (byYear: IHolidaysByYear[]): HolidayRowsByYear[] => {
  byYear.forEach((holidayData) => {
    const holidaysArray: HolidayRow[] = []
    holidayData.holidays.forEach((single) => {
      if (single.multiDayId !== null) {
        if (holidaysArray.filter((holiday) => single.multiDayId === holiday.multiDayId).length === 0) {
          const filtered = holidayData.holidays.filter((holiday) => holiday.multiDayId === single.multiDayId).sort((a, b) => (dayjs(a.date).isBefore(dayjs(b.date)) ? -1 : 1))
          holidaysArray.push({
            ...filtered[0],
            endDate: filtered[filtered.length - 1].date,
            recurring: filtered[0].recurring || false,
          })
        }
      } else {
        delete single.multiDayId
        holidaysArray.push({
          ...single,
          recurring: single.recurring || false,
        })
      }
    })
    holidayData.holidays = holidaysArray
  })
  return byYear
}

type HolidayDeletedBody = Pick<ILocationHolidayDeletedEvent, 'eventType' | 'eventGroup' | 'locationId' | 'id'>
type HolidaysDeletedBody = Pick<ILocationHolidaysDeletedEvent, 'eventType' | 'eventGroup' | 'locationId' | 'ids'>

interface ILocationPage {
  match: {
    params: {
      id: string
      type: string
    }
  }
}

const LocationPage = ({ match }: ILocationPage): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { actionNotifications, eventsNotifications, addActionNotifications } = useContext(notificationStore)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const { notification } = App.useApp()
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()

  const [isLoading, setIsLoading] = useState(true)
  const [location, setLocation] = useState<IGetLocationGeneral>({
    id: '',
    name: '',
    workWeek: [],
    timestamp: '',
    resetQuotas: '',
    rolloverNeverExpireDays: true,
    firstDayOfWeek: 0,
    yearStartDay: 1,
    yearStartMonth: 1,
    timezone: getTimeZoneOrDefaultToUtc(),
    users: [],
    holidays: [],
    isDefault: false,
    leavePolicies: [],
  })
  const [locationHolidays, setLocationHolidays] = useState<HolidayRowsByYear[]>()
  const [leavePolicies, setLeavePolicies] = useState<ILocationGeneralLeavePolicies[]>([])
  const [allLeavePolicies, setAllLeavePolicies] = useState<ILocationGeneralLeavePolicies[]>([])
  const [leaveTypes, setLeaveTypes] = useState<IGetLeaveTypeList[]>([])
  const [locationList, setLocationList] = useState<IGetLocationList[]>([])
  const [selectedTab, setSelectedTab] = useState<LocationTab>(() => (isLocationTab(match.params.type) ? match.params.type : 'general'))
  const [confirmingDefault, setConfirmingDefault] = useState(false)
  const [makingDefault, setMakingDefault] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Values saved inline but not yet visible to the read model, so a refetch triggered by anything
  // else on the page cannot flash the old value back. Entries are stamped and expire: an event can
  // be accepted and then fail in its handler, and without a deadline that value would be re-applied
  // over the truth on every later refetch, for as long as the page stayed open.
  const pendingPatch = useRef<Map<string, { value: unknown; at: number }>>(new Map())

  const inlineEditGroup = useInlineEditGroupState()
  const unsavedGuard = useUnsavedChangesGuard(inlineEditGroup.isDirty, formatMessage({ id: 'locationGeneral.unsavedTitle' }))

  const canEdit = !isLocationAdmin || managedLocationIds.includes(match.params.id)
  // Derived, not latched: the banner asks you to rename, so it has to go away when you do.
  const showRenameWarning = isAutodetectedName(location.name)
  // Set when the header's actions are not this admin's to take, and doubles as their tooltip.
  const headerActionLock = canEdit && !isLocationAdmin ? undefined : formatMessage({ id: 'app.noPermission' })

  const [getLocationByIdLeaveTypesAllLeavePoliciesQuery] = useManualQuery<IGetLocationLeaveTypesAllLeavePoliciesData, { id: string }>(getLocationByIdLeaveTypesAllLeavePolicies)

  useEffect(() => {
    fetchLocation(match.params.id)
  }, [match.params.id])

  // A bulk action writes to these contexts once per user event, and this page's query carries users,
  // every year of holidays and every location's leave policies. One timer shared by both effects
  // turns a fifty-person move into a single refetch, a second after the last event rather than the
  // first - so it lands on the finished list instead of a half-moved one.
  const refetchTimer = useRef<number>()
  const scheduleRefetch = () => {
    window.clearTimeout(refetchTimer.current)
    refetchTimer.current = window.setTimeout(() => {
      fetchLocation(match.params.id)
    }, 1000)
  }
  useEffect(() => () => window.clearTimeout(refetchTimer.current), [])

  useEffect(() => {
    scheduleRefetch()
  }, [actionNotifications])

  // Moving people in from the Users tab posts one event per user under a shared correlation id,
  // which the websocket tracks in eventsNotifications rather than actionNotifications. Without this
  // the page keeps the user list it loaded, and the tab goes on marking the location as empty.
  const isFirstEventsRun = useRef(true)
  useEffect(() => {
    if (isFirstEventsRun.current) {
      isFirstEventsRun.current = false
      return
    }
    scheduleRefetch()
  }, [eventsNotifications])

  // The URL owns the tab, so a deep link and the back button both land where they should. An
  // unknown segment - including a bookmarked /edit from before this page absorbed the form -
  // normalises rather than rendering nothing.
  useEffect(() => {
    if (isLocationTab(match.params.type)) {
      setSelectedTab(match.params.type)
    } else {
      history.replace(`/app/settings/locations/${match.params.id}/general`)
    }
  }, [match.params.type, match.params.id])

  // A pending value belongs to the location it was typed on.
  useEffect(() => {
    pendingPatch.current.clear()
  }, [match.params.id])

  const patchLocation = (patch: Partial<IGetLocationGeneral>) => {
    const now = Date.now()
    Object.entries(patch).forEach(([key, value]) => pendingPatch.current.set(key, { value, at: now }))
    setLocation((prev) => ({ ...prev, ...patch }))
  }

  const { saveFields } = useLocationFieldSave({ location, onPatch: patchLocation })

  // The tab is a route segment, so the navigation guard already catches a tab switch with unsaved
  // changes - there is no second pending-state to track here.
  const goToTab = (key: string) => {
    if (!isLocationTab(key) || key === selectedTab) return
    history.replace(`/app/settings/locations/${match.params.id}/${key}`)
  }

  const discardAndLeave = () => {
    inlineEditGroup.close()
    unsavedGuard.confirmNavigation()
  }

  const fetchLocation = async (id) => {
    try {
      const response = await getLocationByIdLeaveTypesAllLeavePoliciesQuery({ variables: { id } })
      if (!response.data || response.error) throw response.error
      if (!redirectIfEntityNotFound(response.data.getLocation, FrontendUrls.locations, history)) return

      // Set leave types which not exist in this location
      setLeaveTypes(
        response.data.getLeaveTypeList
          .filter((lt) => lt.isActive)
          .filter(({ id: leaveTypeId }) => {
            return !response.data?.getLocation.leavePolicies.some(({ leaveType: locationLeaveType }) => locationLeaveType.id === leaveTypeId)
          })
          .sort((a, b) => (a.position < b.position ? -1 : 1))
      )
      const fetched = response.data.getLocation
      // Drop anything the read model has caught up on - or that has waited long enough that the
      // event is not coming - then re-apply the rest, so an unrelated refetch cannot undo an
      // inline save that is still settling.
      const now = Date.now()
      pendingPatch.current.forEach((entry, key) => {
        const settled = JSON.stringify(fetched[key]) === JSON.stringify(entry.value)
        if (settled || now - entry.at > PENDING_EDIT_TTL_MS) {
          pendingPatch.current.delete(key)
        }
      })
      setLocation({ ...fetched, ...Object.fromEntries([...pendingPatch.current].map(([key, entry]) => [key, entry.value])) })
      setLocationList(response.data.getLocationList)
      const leavePoliciesData = response.data.getLocation.leavePolicies
        .filter((leavePolicy) => !leavePolicy.leaveType.deleted)
        .map((leavePolicy) => {
          return {
            ...leavePolicy,
            index: leavePolicy.leaveType.position,
            key: leavePolicy.leaveType.position,
          }
        })
        .filter((lp) => lp.leaveType.isActive)
      const dataRaw = leavePoliciesData.sort((a, b) => a.leaveType.position - b.leaveType.position)
      setLeavePolicies(dataRaw.map((lp) => decorateLeavePolicyWithQuotaUnits(lp, Boolean(authCompany?.hourlyLeaveAccounting))))

      let allLeavePoliciesData: ILocationGeneralLeavePolicies[] = []
      response.data.getLocationList.forEach((loc: IGetLocationList) => {
        const locationLeavePolicies: ILocationGeneralLeavePolicies[] = loc.leavePolicies
          .filter((lp) => lp.isActive)
          .filter((lp) => !lp.leaveType.deleted)
          .filter((lp) => lp.leaveType.isActive)
          .map((lp) => {
            lp.leaveType.name = `${lp.leaveType.name} (${loc.name})`
            return lp
          })

        allLeavePoliciesData = allLeavePoliciesData.concat(locationLeavePolicies)
      })
      const allDataRaw = sortBy(allLeavePoliciesData, ['leaveType.position', 'leaveType.name'])
      setAllLeavePolicies(allDataRaw.map((lp) => decorateLeavePolicyWithQuotaUnits(lp, Boolean(authCompany?.hourlyLeaveAccounting))))

      setLocationHolidays(groupMultiDayHolidays(response.data.getLocation.holidays))
    } catch (err) {
      logger.error('error fetching location', err)
    } finally {
      // In a finally: as the last statement of the try, any throw above left the page spinning for
      // good with nothing on screen to say why.
      setIsLoading(false)
    }
  }

  // Reported before the post returned: tracking it now would keep the id in the list for good.
  const trackEvent = (correlationId: string, quiet: boolean, alreadySettled = Boolean(settledOutcome(correlationId))): Promise<EventOutcome> => {
    const outcome = watchEvent(correlationId, { quiet, timeoutMs: EVENT_SETTLE_TIMEOUT_MS })
    if (!alreadySettled) addActionNotifications([correlationId])
    // A timed-out event changes no notification, so nothing else would refetch for it.
    void outcome.then(() => scheduleRefetch())
    return outcome
  }

  // Resolves null when the event was not accepted, after saying so.
  const postWithProgress = async (body: HolidayDeletedBody | HolidaysDeletedBody, messageId: string, values?: Record<string, number>): Promise<PostedEvent | null> => {
    try {
      const response = await Api.post('/core/event', body)
      const alreadySettled = Boolean(settledOutcome(response.correlationId))
      // Already reported: a progress notification now would replace that result and spin for good.
      if (!alreadySettled) {
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: messageId }, values),
          icon: <LoadingOutlined />,
          duration: 0,
        })
      }
      return { settled: trackEvent(response.correlationId, false, alreadySettled) }
    } catch (err) {
      logger.error('error posting holidays event', err)
      notification.error({ message: formatMessage({ id: 'error.generic' }) })
      return null
    }
  }

  // No wait before posting: the tab's queue starts a holiday event only after the previous one settled.
  const deleteHoliday = (holidayId: string): Promise<PostedEvent | null> =>
    postWithProgress({ eventType: 'LOCATION_HOLIDAY_DELETED', eventGroup: 'LOCATION', locationId: match.params.id, id: holidayId }, 'location.holidays.deleteInProgress')

  const deleteHolidays = (ids: string[]): Promise<PostedEvent | null> =>
    postWithProgress({ eventType: 'LOCATION_HOLIDAYS_DELETED', eventGroup: 'LOCATION', locationId: match.params.id, ids }, 'location.holidays.bulkDeleteInProgress', {
      count: ids.length,
    })

  const trackInlineSave = (correlationId: string) => trackEvent(correlationId, true)

  const deleteLocation = async () => {
    try {
      const response = await Api.post('/core/event', {
        eventType: 'LOCATION_DELETED',
        eventGroup: 'LOCATION',
        locationId: location.id,
      })
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'location.deleteInProgress' }, { locationName: location.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      addActionNotifications([response.correlationId])
      history.push(FrontendUrls.locations)
    } catch (err) {
      logger.error('error deleting location', err)
      notification.error({ message: formatMessage({ id: 'error.generic' }) })
    }
  }

  const confirmDelete = () => setConfirmingDelete(true)

  const holidaysThisYear = countHolidaysThisYear(locationHolidays)

  // A tab whose section is empty carries the marker, so the gap is visible without opening it.
  const tabLabel = (textId: string, markId: string, missing: boolean) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xxs }}>
      <IntlMessages id={textId} />
      {missing && <RecordCardMissing label={formatMessage({ id: markId }, { count: 0 })} />}
    </span>
  )

  const locationsTabs: TabsProps['items'] = [
    {
      key: 'general',
      label: <IntlMessages id="app.general" />,
      children: (
        <LocationGeneral
          location={location}
          otherLocations={locationList}
          locale={locale.locale || LocaleEnum.en}
          canEdit={canEdit}
          lockReason="locationGeneral.lockedNotYours"
          workdayEnabled={isWorkDayFeatureAllowed(featureFlags, authCompany)}
          onPatch={patchLocation}
        />
      ),
    },
    {
      key: 'leave-policies',
      label: tabLabel('locations.leavePolicies', 'locationPlace.card.leaveTypes', leavePolicies.length === 0),
      children: (
        <LocationLeavePolicies
          leavePoliciesList={leavePolicies}
          location={location}
          leaveTypes={leaveTypes}
          allLeavePolicies={allLeavePolicies}
          hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
          companyPlan={effectivePlan as SubscriptionPlan}
          canEdit={canEdit}
        />
      ),
    },
    {
      key: 'holidays',
      label: tabLabel('app.holidays', 'locationPlace.card.holidays', holidaysThisYear === 0),
      children: (
        <LocationHolidays
          locationId={location.id}
          locationName={location.name}
          active={selectedTab === 'holidays'}
          holidays={locationHolidays ?? EMPTY_HOLIDAYS}
          workHours={location.workHours}
          workWeek={location.workWeek}
          firstDayOfWeek={location.firstDayOfWeek}
          locale={locale.locale || LocaleEnum.en}
          countryCode={location.holidaysImportPlace?.country ?? location.countryCode}
          subdivisionCode={location.holidaysImportPlace ? location.holidaysImportPlace.state : location.subdivisionCode}
          canEdit={canEdit}
          onEventPosted={trackInlineSave}
          onPendingExpired={scheduleRefetch}
          onDeleteHoliday={deleteHoliday}
          onDeleteHolidays={deleteHolidays}
        />
      ),
    },
    {
      key: 'users',
      label: tabLabel('app.users', 'locationPlace.card.employees', (location.users || []).length === 0),
      children: <LocationUsers locationId={location.id} locationName={location.name} active={selectedTab === 'users'} />,
    },
  ]

  return (
    <div className="main-content">
      {isLoading ? (
        <CircularProgress />
      ) : (
        <div>
          <PageHeader
            title={location.name}
            // The badge is the statement; a disabled "Make default" beside it would only repeat
            // what it already says.
            badge={location.isDefault && <StatusBadge tone="neutral">{formatMessage({ id: 'locations.default' })}</StatusBadge>}
            parent={[{ label: <IntlMessages id="app.locations" />, to: FrontendUrls.locations }]}
            actions={
              <>
                {!location.isDefault && (
                  // Stays on screen when an admin cannot use it, and says why - a button that simply
                  // vanishes leaves them wondering whether the action exists at all.
                  <Tooltip title={headerActionLock}>
                    <span>
                      <Button disabled={Boolean(headerActionLock)} onClick={() => setConfirmingDefault(true)}>
                        <IntlMessages id="locationGeneral.makeDefault" />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={headerActionLock ?? (location.isDefault ? formatMessage({ id: 'locations.deleteLocation.disabled' }) : undefined)}>
                  <span>
                    <Button variant="outline-danger" disabled={Boolean(headerActionLock) || location.isDefault} onClick={confirmDelete}>
                      <IntlMessages id="locations.deleteLocation" />
                    </Button>
                  </span>
                </Tooltip>
              </>
            }
          />
          {showRenameWarning && (
            <Banner
              tone="warning"
              message={formatMessage({ id: 'locations.pleaseRenameLocationName' })}
              description={<IntlMessages id="locations.automaticallyGeneratedName" values={{ team: autodetectedTeams(location.name), t: (chunks) => <strong>{chunks}</strong> }} />}
            />
          )}
          <div className="main-content-body">
            <div className="location-body vt-location-tabs">
              <InlineEditGroupContext.Provider value={inlineEditGroup}>
                <Tabs activeKey={selectedTab} onChange={goToTab} items={locationsTabs} />
              </InlineEditGroupContext.Provider>
            </div>
          </div>
          {confirmingDelete && (
            <ConfirmModal
              open
              title={formatMessage({ id: 'locations.deleteLocationTitle' }, { name: location.name })}
              message={
                <>
                  {formatMessage({ id: 'locations.deleteLocationConfirm' }, { name: location.name })}
                  <br />
                  <br />
                  <WarningOutlined /> {formatMessage({ id: 'locations.deleteLocationConfirmInitialRollover' })}
                </>
              }
              primaryAction={{
                label: formatMessage({ id: 'locations.deleteLocation' }),
                variant: 'primary-danger',
                loading: deleting,
                onClick: async () => {
                  setDeleting(true)
                  try {
                    await deleteLocation()
                  } finally {
                    setDeleting(false)
                    setConfirmingDelete(false)
                  }
                },
              }}
              secondaryAction={{ label: formatMessage({ id: 'app.cancel' }), onClick: () => setConfirmingDelete(false) }}
              onClose={() => setConfirmingDelete(false)}
            />
          )}
          {confirmingDefault && (
            <ConfirmModal
              open
              title={formatMessage({ id: 'locationGeneral.makeDefaultTitle' }, { name: location.name })}
              message={formatMessage({ id: 'locationGeneral.makeDefaultReplaces' }, { name: locationList.find((item) => item.isDefault && item.id !== location.id)?.name ?? '' })}
              primaryAction={{
                label: formatMessage({ id: 'locationGeneral.makeDefault' }),
                loading: makingDefault,
                onClick: async () => {
                  setMakingDefault(true)
                  try {
                    await saveFields({ isDefault: true }, { track: true })
                    setConfirmingDefault(false)
                  } finally {
                    setMakingDefault(false)
                  }
                },
              }}
              secondaryAction={{ label: formatMessage({ id: 'app.cancel' }), onClick: () => setConfirmingDefault(false) }}
              onClose={() => setConfirmingDefault(false)}
            />
          )}
          {unsavedGuard.pendingLocation && (
            <ConfirmModal
              open
              title={formatMessage({ id: 'locationGeneral.unsavedTitle' })}
              message={formatMessage({ id: 'locationGeneral.unsavedMessage' })}
              primaryAction={{
                label: formatMessage({ id: 'locationGeneral.unsavedDiscard' }),
                variant: 'primary-danger',
                onClick: discardAndLeave,
              }}
              secondaryAction={{
                label: formatMessage({ id: 'locationGeneral.unsavedKeepEditing' }),
                onClick: unsavedGuard.cancelNavigation,
              }}
              onClose={unsavedGuard.cancelNavigation}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default withRouter(LocationPage)
